/** Linear undo/redo history of immutable snapshots. */
export class History<T> {
  private past: T[] = [];
  private future: T[] = [];

  constructor(
    private present: T,
    private readonly limit = 100,
  ) {}

  get current(): T {
    return this.present;
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  /** Records a new state; clears the redo stack. */
  push(next: T): void {
    this.past.push(this.present);
    if (this.past.length > this.limit) this.past.shift();
    this.present = next;
    this.future = [];
  }

  /**
   * Ends a continuous edit (a drag): `from` becomes the undo step and `next` the present,
   * whatever intermediate states were set with replace().
   */
  pushFrom(from: T, next: T): void {
    this.past.push(from);
    if (this.past.length > this.limit) this.past.shift();
    this.present = next;
    this.future = [];
  }

  /** Replaces the present state without creating an undo step (e.g. during a drag). */
  replace(next: T): void {
    this.present = next;
  }

  undo(): T | null {
    const prev = this.past.pop();
    if (prev === undefined) return null;
    this.future.push(this.present);
    this.present = prev;
    return prev;
  }

  redo(): T | null {
    const next = this.future.pop();
    if (next === undefined) return null;
    this.past.push(this.present);
    this.present = next;
    return next;
  }

  /** Starts over from `state` (e.g. when a preset is loaded) keeping the old one undoable. */
  reset(state: T): void {
    this.push(state);
  }
}
